import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import List from '@material-ui/core/List';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemText from '@material-ui/core/ListItemText';
import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles/createTheme';

import ErrorOutlineOutlinedIcon from '@material-ui/icons/ErrorOutlineOutlined';
import MobileFriendlyOutlinedIcon from '@material-ui/icons/MobileFriendlyOutlined';
import PhonelinkEraseOutlinedIcon from '@material-ui/icons/PhonelinkEraseOutlined';
import ContactPhoneOutlinedIcon from '@material-ui/icons/ContactPhoneOutlined';
import ContactMailOutlinedIcon from '@material-ui/icons/ContactMailOutlined';
import ContactPhoneIcon from '@material-ui/icons/ContactPhone';
import ContactMailIcon from '@material-ui/icons/ContactMail';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import AssignmentIndOutlinedIcon from '@material-ui/icons/AssignmentIndOutlined';
import AssignmentReturnedOutlinedIcon from '@material-ui/icons/AssignmentReturnedOutlined';
import ScreenLockPortraitOutlinedIcon from '@material-ui/icons/ScreenLockPortraitOutlined';
import ForwardOutlinedIcon from '@material-ui/icons/ForwardOutlined';
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined';
import AttachMoneyOutlinedIcon from '@material-ui/icons/AttachMoneyOutlined';
import FeaturedVideoOutlinedIcon from '@material-ui/icons/FeaturedVideoOutlined';
import SettingsApplicationsOutlinedIcon from '@material-ui/icons/SettingsApplicationsOutlined';
import RotateLeftOutlinedIcon from '@material-ui/icons/RotateLeftOutlined';
import CancelPresentationOutlinedIcon from '@material-ui/icons/CancelPresentationOutlined';
import ListIcon from '@material-ui/icons/List';
import DescriptionIcon from '@material-ui/icons/Description';
import DescriptionIconOutlined from '@material-ui/icons/DescriptionOutlined';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import ArchiveIcon from '@material-ui/icons/Archive';
import AssignmentLateOutlinedIcon from '@material-ui/icons/AssignmentLateOutlined';
import LockOpenIcon from '@material-ui/icons/LockOpen';

import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import compose from 'helpers/compose';
import Background from 'components/Layout/Background';

import ToolsForms, { tabNames } from './ToolsForms';
import { ToolsStore } from 'stores/admin/toolsStore';
import { RootStore } from 'stores/rootStore';
import { EdwardColors, Environment } from 'enums';
import { Permission } from 'helpers/permissions';
import { APP_CONFIG } from 'appConfig';

interface Props {
  rootStore: RootStore;
}

const menuWidth = 425;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menu: {
      width: `${menuWidth}px`,
      maxWidth: `${menuWidth}px`,
      flexBasis: `${menuWidth}px`,
      '& nav': { background: '#fff' },
      '& .MuiSvgIcon-root': {
        marginRight: theme.spacing(1),
        color: EdwardColors.SECONDARY,
      },
    },
    content: {
      width: `calc(100% - ${menuWidth}px)`,
      maxWidth: `calc(100% - ${menuWidth}px)`,
      flexBasis: `calc(100% - ${menuWidth}px)`,
      '& .LIGHT': {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4),
      },
      '& h1': {
        marginBottom: theme.spacing(4),
      },
      '& .MuiContainer-root.MuiContainer-maxWidthMd': {
        margin: 0,
        padding: theme.spacing(0, 2, 0, 2),
      },
    },
    item: {},
    selected: {
      '& .MuiTypography-body1': { fontWeight: 500 },
      backgroundColor: EdwardColors.LIGHT,
      '&:hover': {
        backgroundColor: EdwardColors.LIGHT,
      },
    },
    divider: {
      marginBottom: theme.spacing(0.5),
      borderBottom: 'solid #ccc 1px',
    },
  }),
);

interface Tab {
  name: tabNames;
  icon: React.ReactElement;
  divider?: boolean;
  message?: boolean;
  permission?: Permission;
  env?: Environment[];
}

const Tools: React.FC<Props> = ({ rootStore }) => {
  const { t } = useTranslation();
  const [store] = useState(new ToolsStore(rootStore));
  const { confirmDialog, resultDialog } = store;
  const [tab, setTab] = useState(0);
  const classes = useStyles();

  useEffect(() => {
    store.fetchOptions();
  }, [store]);

  const tabs: Tab[] = [
    { name: 'errMessage', icon: <ErrorOutlineOutlinedIcon /> },
    { name: 'signature_state', icon: <MobileFriendlyOutlinedIcon /> },
    { name: 'signature_cancel', icon: <PhonelinkEraseOutlinedIcon /> },
    { name: 'request_cancel', icon: <CancelPresentationOutlinedIcon /> },
    { name: 'account_history', icon: <ListIcon /> },
    { name: 'change_cc', icon: <AssignmentIndOutlinedIcon /> },
    { name: 'change_id', icon: <FeaturedVideoOutlinedIcon />, message: true },
    { name: 'reactivate_user', icon: <LockOpenIcon /> },
    { name: '2fa', icon: <ScreenLockPortraitOutlinedIcon />, message: true },
    { name: 'phone', icon: <ContactPhoneOutlinedIcon /> },
    { name: 'change_advisor_phone', icon: <ContactPhoneIcon /> },
    { name: 'email', icon: <ContactMailOutlinedIcon />, message: true },
    { name: 'change_advisor_email', icon: <ContactMailIcon />, message: true },
    { name: 'taxMode', icon: <AssignmentLateOutlinedIcon /> },
    { name: 'generate_pdf', icon: <DescriptionIcon /> },
    { name: 'generate_pdf_BoPo', icon: <DescriptionIconOutlined /> },
    { name: 'generate_amendment', icon: <DescriptionIconOutlined /> },
    { name: 'delete_statement', icon: <DeleteOutlineOutlinedIcon /> },
    { name: 'event', icon: <ForwardOutlinedIcon />, message: true },
    { name: 'reset_advisor', icon: <RotateLeftOutlinedIcon /> },
    { name: 'delete_account', icon: <DeleteOutlineOutlinedIcon />, message: true },
    { name: 'generate_csv', icon: <ArchiveIcon /> },
    { name: 'claim', icon: <AttachMoneyOutlinedIcon />, message: true },
    {
      name: 'account_transaction',
      icon: <AttachMoneyOutlinedIcon />,
      env: [Environment.LOCAL, Environment.DEV, Environment.TEST],
    },
    { name: 'transfer_cc', icon: <AssignmentReturnedOutlinedIcon />, message: true },
    { name: 'set_partnercode', icon: <SupervisorAccountIcon />, divider: true },
    {
      name: 'activate_account',
      icon: <VerifiedUserOutlinedIcon />,
      message: true,
      permission: Permission.ACCESS_ADMIN,
    },
    { name: 'set_constant', icon: <SettingsApplicationsOutlinedIcon />, permission: Permission.ACCESS_ADMIN },
  ];

  if (!store.options) return null;

  return (
    <Grid container>
      <Grid item className={classes.menu}>
        <List component="nav">
          {tabs.map((mTab, i) =>
            (!mTab.env || (APP_CONFIG.env && mTab.env.includes(APP_CONFIG.env as Environment))) &&
            (!mTab.permission || rootStore.stores.userStore.checkPermissions(mTab.permission)) ? (
              <MenuItem
                key={mTab.name}
                onClick={() => setTab(i)}
                className={`${classes.item} ${tab === i ? classes.selected : ''} ${
                  mTab.divider ? classes.divider : ''
                }`}
              >
                {mTab.icon}
                <ListItemText primary={t(`backOffice_${mTab.name}_title`)} />
              </MenuItem>
            ) : null,
          )}
        </List>
      </Grid>
      <Grid item className={classes.content}>
        <Background inside>
          <Container maxWidth="md">
            <Typography variant="h1">{t(`backOffice_${tabs[tab].name}_title`)}</Typography>

            <ToolsForms name={tabs[tab].name} message={tabs[tab].message} store={store} key={tabs[tab].name} />
          </Container>
        </Background>

        <ConfirmDialog noHeader isOpen={confirmDialog} confirm={store.confirmEffect} close={store.hideConfirmDialog}>
          {t(store.confirmMessage)}
        </ConfirmDialog>

        <ConfirmDialog noHeader isOpen={resultDialog} confirm={store.hideResultDialog}>
          {Array.isArray(store.message) ? (
            store.message.map((msg, i) => (
              <Typography
                key={i}
                dangerouslySetInnerHTML={{
                  __html: t(msg.message, { interpolation: { escapeValue: false }, ...msg.messageValues }),
                }}
              />
            ))
          ) : (
            <Typography
              dangerouslySetInnerHTML={{
                __html: t(store.message, { interpolation: { escapeValue: false }, ...store.messageValues }),
              }}
            />
          )}
        </ConfirmDialog>
      </Grid>
    </Grid>
  );
};

export default compose(inject('rootStore'), withRouter, observer)(Tools);
